import { msg } from '@lingui/core/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useMemo, useState } from 'react';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { history } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { useCopyBoardTemplateMutation } from 'modules/template-editor/store/board-templates-api';
import { addLike } from 'modules/template-editor/store/templates/actions';
import { useActiveBoardTemplate } from 'modules/template-editor/ui/hooks/use-active-board-template';
import { useOwnerBoardTemplates } from 'modules/template-editor/ui/hooks/use-owner-board-templates';

const PRESET: TemplatePresetsNameList = 'copy';
// eslint-disable-next-line lingui/no-unlocalized-strings -- имя копии уходит в базу, это данные, а не подпись интерфейса
const POSTFIX = ' (копия)';

function useCopyModal(templateId: number) {
  const { t } = useLingui();
  const dispatchWithToast = useDispatchWithToast();
  const toastApiError = useToastApiError();
  const { info } = useOrganizer();
  // Шаблон каталога: его нет в списке организации, хук возьмёт его из слайса.
  const activeTemplate = useActiveBoardTemplate();
  const { templates } = useOwnerBoardTemplates();
  const [copyTemplate, { isLoading }] = useCopyBoardTemplateMutation();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561). Копия к ответу
    // уже в кэше списка организации — страница шаблонов показывает её без перезапроса (IMGM-1566).
    async (values: Values) => {
      if (!info) {
        console.warn({ event: 'failed to copy template: info is undefined', info });
        return;
      }

      const { width, height, name } = values;
      const resolution = { width: +width, height: +height };
      try {
        await copyTemplate({ ownerId: info.id, templateId, name: name.toString(), resolution }).unwrap();
      } catch (error) {
        toastApiError(error, msg`Не удалось создать копию`);
        return;
      }
      close();
      dispatchWithToast(addLike({ templateId: templateId, userId: info.id }), { error: msg`Не удалось поставить лайк` });
      history.push(Pages.TEMPLATE.INDEX, { isCoppedTemplate: true });
    },
    [close, copyTemplate, dispatchWithToast, info, templateId, toastApiError],
  );

  const initialValue = {
    width: activeTemplate?.resolution.width?.toString() ?? '1920',
    height: activeTemplate?.resolution.height?.toString() ?? '1080',
    name: activeTemplate?.name ? activeTemplate?.name + POSTFIX : '',
    visibility: getVisibilityOptions().find((option) => option.value === activeTemplate?.visibility) ?? getVisibilityOptions()[0],
    access: getAccessOptions().find((option) => option.value === activeTemplate?.access) ?? getAccessOptions()[0],
  };

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isLoading}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCopyModal;
